import "react-image-crop/dist/ReactCrop.css";

import { AddIconNew, AddPhotoIcon, CropMaxIcon, CropMinIcon, DogIcon } from "../Icons";
import { Box, Button, IconButton, Slider, styled } from "@mui/material";
import React, { FC, useRef, useState } from "react";
import ReactCrop, {
  Crop,
  PixelCrop,
  centerCrop,
  makeAspectCrop,
} from "react-image-crop";
import { iconActionStyle, style } from "./UploadImageButton.style";
import { resizeFile, uuid } from "../../utils";

import { DialogCustom } from "../../styles/styledComponents";
import { LoadingOverlay } from "..";
import { canvasPreview } from "./canvasPreview";
import { toastMessageActions } from "../../state/request";
import { useDebounceEffect } from "./useDebounceEffect";
import { useDispatch } from "react-redux";

function centerAspectCrop(
  mediaWidth: number,
  mediaHeight: number,
  aspect: number,
) {
  return centerCrop(
    makeAspectCrop(
      {
        unit: "%",
        width: 90,
      },
      aspect,
      mediaWidth,
      mediaHeight,
    ),
    mediaWidth,
    mediaHeight,
  );
}

const Input = styled("input")({
  display: "none",
});

const Image = styled("img")({
  borderRadius: "50%",
  height: "100%",
  width: "100%",
  objectFit: "cover",
});

interface IUploadButtonProps {
  id: string;
  icon?: React.ReactElement;
  accept?: string;
  url?: string;
  aspect?: number;
  onChange?: (file: File) => void;
}

const UploadImageButton: FC<IUploadButtonProps> = ({
  id,
  icon = <DogIcon width={48} height={48} />,
  accept = "image/tiff, image/jpeg, image/png, image/bmp, image/webp",
  url,
  aspect = 16 / 9,
  onChange,
}) => {
  const dispatch = useDispatch();
  const [previewFile, setSelectedFile] = useState();
  const [uncroppedImage, setUncroppedImage] = useState<string>();
  const previewCanvasRef = useRef<HTMLCanvasElement>(null);
  const imgRef = useRef<HTMLImageElement>(null);
  const [crop, setCrop] = useState<Crop>();
  const [completedCrop, setCompletedCrop] = useState<PixelCrop>();
  const [scale, setScale] = useState(1);
  const [openDialog, setOpenDialog] = useState(false);
  const [isResizing, setIsResizing] = useState(false);

  const onImageLoad = (e: React.SyntheticEvent<HTMLImageElement>) => {
    const { width, height } = e.currentTarget;

    setCrop(centerAspectCrop(width, height, aspect));
  };

  const onSelectFile = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];

    if (!file) return;
    if (file.size > 10000000) {
      dispatch(toastMessageActions.setGlobalAlert({
        severity: "error",
        message: "Maximum uploaded file size allowed is 10mb",
      }));

      return;
    }

    setIsResizing(true);
    const image = await resizeFile(file);

    setUncroppedImage(image);
    setOpenDialog(true);
    setIsResizing(false);
  };

  const handleCrop = () => {
    previewCanvasRef?.current?.toBlob((blob) => {
      if (!blob) {
        throw new Error("Failed to create blob");
      }
      const file = new File([blob], uuid(), {
        type: blob.type,
        lastModified: new Date().getTime(),
      });

      onChange && onChange(file);
      const reader = new FileReader();

      reader.onload = (e: any) => {
        setSelectedFile(e.target?.result);
        setOpenDialog(false);
      };
      reader.readAsDataURL(file);
    });
  };

  useDebounceEffect(async () => {
    if (
      completedCrop?.width &&
      completedCrop?.height &&
      imgRef.current &&
      previewCanvasRef.current
    ) {
      canvasPreview(
        imgRef.current,
        previewCanvasRef.current,
        completedCrop,
        scale,
      );
    }
  },
  100,
  [completedCrop, scale, uncroppedImage],
  );

  return (
    <>
      {isResizing && <LoadingOverlay />}

      <form encType={"multipart/form-data"}>
        <label htmlFor={id}>
          <Box sx={{ position: "relative" }} component="span">
            <Input
              id={id}
              accept={accept}
              multiple={false}
              type="file"
              onChange={onSelectFile}
            />
            <IconButton aria-label="upload picture" component="span" sx={style}>
              {
                (previewFile || url) ? <Image src={previewFile || url} /> : <span>{icon}</span>
              }
            </IconButton>

            {/* {!url ? <Box sx={warningIconStyle}>
              <Image src="/assets/icons/Notification bubble.svg" alt={"info"}/>
            </Box> : null} */}

            <Box sx={iconActionStyle}>
              {(previewFile || url) ? <AddPhotoIcon /> : <AddIconNew />}
            </Box>
          </Box>

          {(previewFile || url) ? null :
            <p
              className="mt-4 text-gray-1 geograph-regular text-sm"
            >
              Tap to upload a photo
            </p>
          }
        </label>
      </form>

      <DialogCustom
        open={openDialog}
        onClose={() => setOpenDialog(false)}
      >
        {!!uncroppedImage && (
          <ReactCrop
            crop={crop}
            onChange={(_, percentCrop) => setCrop(percentCrop)}
            onComplete={(c) => setCompletedCrop(c)}
            aspect={aspect}
          >
            <img
              ref={imgRef}
              alt="Crop me"
              src={uncroppedImage}
              style={{ transform: `scale(${scale})` }}
              onLoad={onImageLoad}
            />
          </ReactCrop>
        )}

        <Box sx={{ display: "flex", flexDirection: "row", paddingTop: 2, gap: 2 }}>
          <div>
            <CropMinIcon />
          </div>
          <Slider
            aria-label="Scale"
            value={scale}
            step={0.1}
            min={1}
            max={4}
            onChange={(e: any) => setScale(Number(e.target?.value))}
            disabled={!previewFile}
          />
          <div>
            <CropMaxIcon />
          </div>
        </Box>

        {!!completedCrop && (
          <canvas
            ref={previewCanvasRef}
            style={{
              objectFit: "contain",
              width: completedCrop.width,
              height: completedCrop.height,
              maxHeight: "100%",
              display: "none",
            }}
          />
        )}

        <Box sx={{ display: "flex", flexDirection: "row", paddingTop: 2, gap: 2 }}>
          <Button
            fullWidth
            variant="outlined"
            color="primary"
            type="button"
            size="small"
            onClick={() => setOpenDialog(false)}
          >
            Cancel
          </Button>
          <Button
            fullWidth
            color="primary"
            type="button"
            size="small"
            onClick={handleCrop}
          >
            Crop
          </Button>
        </Box>

      </DialogCustom>
    </>
  );
};

export default UploadImageButton;
